import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { FilterChip } from "@volca/ui/components/ui/filter-chip";
import { dayjs } from "@/core/utils/date";
import type { ReportDataset, ReportDates } from "@/profitability/types";

/** A day inside the report's period, or the whole period. */
export type Lens = { start_date: string; end_date: string } | null;

/** Datasets that date their rows, and can therefore be narrowed to a day. */
export const scopableDatasets = (datasets: ReportDataset[]) =>
  datasets.filter((dataset) => !!dataset.period_field);

/**
 * Every day of the run. A report may cover up to two years, and the menu
 * scrolls, so the last day of a long one is reachable like any other. Capping
 * the list made those days unselectable with nothing to say why.
 */
const days = (dates: ReportDates) => {
  const result: string[] = [];
  let day = dayjs(dates.start_date);
  const last = dayjs(dates.end_date);
  while (!day.isAfter(last)) {
    result.push(day.format("YYYY-MM-DD"));
    day = day.add(1, "day");
  }
  return result;
};

/**
 * Narrows the page to one day of the report without running anything: the
 * report is the saved result for its whole period, and this filters it. Rows
 * that carry no date are left alone, which the chip says, because a purchase
 * order cannot honestly be attributed to a day.
 */
export const PeriodLens = ({
  dates,
  datasets,
  value,
  onChange,
}: {
  dates: ReportDates;
  datasets: ReportDataset[];
  value: Lens;
  onChange: (lens: Lens) => void;
}) => {
  const scopable = scopableDatasets(datasets);
  if (!scopable.length) return null;
  const whole = datasets.length === scopable.length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <FilterChip
            label="Showing"
            value={
              value
                ? dayjs(value.start_date).format("ddd, MMM D")
                : "Whole period"
            }
            active={!!value}
            onClear={value ? () => onChange(null) : undefined}
            icon={value ? <XIcon className="hidden" /> : undefined}
          />
        }
      />
      <DropdownMenuContent
        align="start"
        className="max-h-80 min-w-56 overflow-y-auto overscroll-contain"
      >
        <DropdownMenuRadioGroup
          value={value?.start_date ?? "all"}
          onValueChange={(day) =>
            onChange(day === "all" ? null : { start_date: day, end_date: day })
          }
        >
          <DropdownMenuLabel>
            {whole ? "Show one day" : "Show one day, where rows carry a date"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioItem closeOnClick value="all">
            Whole period
          </DropdownMenuRadioItem>
          {days(dates).map((day) => (
            <DropdownMenuRadioItem closeOnClick key={day} value={day}>
              {dayjs(day).format("ddd, MMM D")}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
